import { useEffect, useState } from "react";
import { X, Ticket as TicketIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * NewTicketModal — open a customer support ticket against an existing company.
 * ticket_no is auto-generated on the backend if we skip it here.
 */
export function NewTicketModal({ open, onOpenChange, onCreated }) {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    company_id: "",
    subject: "",
    description: "",
    category: "technical",
    priority: "medium",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/customer-options").then(r => setCompanies(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      setForm({ company_id: "", subject: "", description: "", category: "technical", priority: "medium" });
    }
  }, [open]);

  async function submit() {
    if (!form.company_id) { toast.error("Pick a company"); return; }
    if (!form.subject.trim()) { toast.error("Subject is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tickets", {
        company_id: form.company_id,
        subject: form.subject.trim(),
        description: form.description.trim() || null,
        category: form.category,
        priority: form.priority,
        status: "open",
      });
      toast.success(`Ticket ${data.ticket_no} opened`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open ticket");
    } finally { setBusy(false); }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#12324A]/45 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => onOpenChange(false)}
      data-testid="new-ticket-modal"
    >
      <div
        className="w-full max-w-lg bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#C9DCE6] bg-[#E7F5FB]">
          <span className="w-9 h-9 rounded-xl bg-white border border-[#C9DCE6] text-[#07539A] flex items-center justify-center"><TicketIcon size={15} /></span>
          <div className="font-display text-[18px] flex-1 text-[#12324A]">Open a ticket</div>
          <button onClick={() => onOpenChange(false)} data-testid="nt-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A] flex items-center justify-center">
            <X size={15} />
          </button>
        </div>

        <div className="p-5 space-y-3.5">
          <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
            <label className="text-[11px] font-semibold text-[#455A64]">Company</label>
            <select
              value={form.company_id}
              onChange={e => setForm({ ...form, company_id: e.target.value })}
              data-testid="nt-company"
              className="w-full h-10 mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 text-[13px] text-[#243B53] outline-none"
            >
              <option value="">— Pick a customer —</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name} · {c.industry}</option>
              ))}
            </select>
          </div>

          <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
            <label className="text-[11px] font-semibold text-[#455A64]">Subject</label>
            <input
              value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
              placeholder="e.g. Dashboard slow on Vapi plant"
              data-testid="nt-subject"
              className="w-full h-10 mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 text-[13px] text-[#243B53] outline-none"
            />
          </div>

          <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
            <label className="text-[11px] font-semibold text-[#455A64]">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="What happened? Steps to reproduce, impact…"
              rows={3}
              data-testid="nt-description"
              className="w-full mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 py-2 text-[13px] text-[#243B53] resize-none outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
              <label className="text-[11px] font-semibold text-[#455A64]">Category</label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                data-testid="nt-category"
                className="w-full h-10 mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 text-[13px] text-[#243B53] outline-none"
              >
                <option value="technical">Technical</option>
                <option value="billing">Billing</option>
                <option value="training">Training</option>
                <option value="feature_request">Feature request</option>
                <option value="bug">Bug</option>
                <option value="onboarding">Onboarding</option>
              </select>
            </div>
            <div className="rounded-2xl bg-white border border-[#C9DCE6] p-3">
              <label className="text-[11px] font-semibold text-[#455A64]">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
                data-testid="nt-priority"
                className="w-full h-10 mt-1 bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] rounded-xl px-3 text-[13px] text-[#243B53] outline-none"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#C9DCE6] bg-white/70">
          <button onClick={() => onOpenChange(false)} data-testid="nt-cancel" className="h-10 px-4 rounded-xl text-[13px] text-[#607D8B] hover:text-[#07539A]">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="nt-submit"
            className="h-10 px-4 rounded-xl bg-[#07539A] hover:bg-[#063F73] !text-white text-[13px] font-medium flex items-center gap-2 disabled:opacity-50 shadow-[0_7px_18px_rgba(7,83,154,0.16)]"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <TicketIcon size={13} />}
            Open ticket
          </button>
        </div>
      </div>
    </div>
  );
}
